import {
  MAIN_MACHINE_ID,
  SELECT_DRIVE_INPUTS,
  TPlayResultTypes,
  TRefEventOutcomes,
  YARDAGE_SELECTOR_BUTTONS,
} from '@/constants';
import { getResultingDownAndDistance } from '@/stores/util/playFlowHelper';
import {
  IMachineContext,
  IStateMetaData,
  IYardageSelectionEvent,
  TCoinTossStates,
  TInputTypes,
  TMachineActions,
  TMachineEvents,
  TMachineStates,
  TPenaltyDriveResult,
  TPlayResults,
  TRefEventsActions,
  TRefEventStates,
  TRegularPlayEvents,
  TRegularPlayStates,
  TWorkflowType,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import {
  CONFIRM_BASIC_META,
  HANDLE_YARDAGE_TOGGLES_META,
  handleFumbleResult,
  handleYardageToggle,
  isTurnoverResult,
  pushResultType,
} from '../utils';
import { REGULAR_PLAY_REF_WF } from './refEventFlows/regularPlay';
import { last } from 'lodash';
import { onPassResultSelected, onPlayDesignSelected } from './nextActions/regularPlay';

const RESULT_TYPES_TO_KEEP_ON_FIRST_DOWN = [TPlayResultTypes.PASS_COMP, TPlayResultTypes.RUSH_ATTENPT];

export const REGULAR_PLAY_MACHINE: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TRegularPlayStates.SELECT_PLAY_DESIGN,
  states: {
    [TRegularPlayStates.SELECT_PLAY_DESIGN]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Play Design',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onPlayDesignSelected,
        onBack({ send, context, playFlowState }) {
          const backEvt = context.inputForNextComponent.backTo;

          let currentWorkflow = null;
          if (backEvt && backEvt.includes(TWorkflowType.FIELD_GOAL)) {
            currentWorkflow = TWorkflowType.FIELD_GOAL;
          }

          // clear yardLine if back to coin toss
          if (backEvt && backEvt.includes(TRegularPlayEvents.BACK_TO_COIN_TOSS)) {
            playFlowState.$patch({ yardLine: null });
          }

          send(backEvt || TRegularPlayEvents.BACK_TO_IDEL, { ctxUpdate: { currentWorkflow } });
        },
      } as IStateMetaData,
      on: {
        [TRegularPlayEvents.BACK_TO_FAKE_FIELD_GOAL]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_FIELD_GOAL}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_FAKE_PUNT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_PUNT}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_IDEL]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_COIN_TOSS]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_COIN}.${TCoinTossStates.SELECT_SIDE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_PASS_SELECTED]: {
          target: TRegularPlayStates.SELECT_PASS_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_RESULT_SELECTED]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.FUMBLE]: {
          target: TRegularPlayStates.SELECT_FUMBLE_RECOVERY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.REF_EVENT]: {
          target: TRegularPlayStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.SELECT_FUMBLE_RECOVERY]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Fumble Result',
          showLogo: true,
          showBackBtn: true,
        },
        onNext({ send, generalState, playFlowState, payload, context }) {
          handleFumbleResult(
            TRegularPlayEvents.FUMBLE_RECOVERY,
            TRegularPlayEvents.BACK,
            TRegularPlayEvents.BACK_TO_FUMBLE_RECOVERY,
            payload,
            send,
            playFlowState,
            generalState,
            context
          );
        },
        onBack({ send }) {
          send(TRegularPlayEvents.BACK);
        },
      } as IStateMetaData,
      on: {
        [TRegularPlayEvents.FUMBLE_RECOVERY]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.PENALTY_SELECTED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK]: {
          target: TRegularPlayStates.SELECT_PLAY_DESIGN,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.COPY_PREV_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.SELECT_PASS_RESULT]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Pass',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onPassResultSelected,
        onBack({ send }) {
          send(TRegularPlayEvents.BACK);
        },
      } as IStateMetaData,
      on: {
        [TRegularPlayEvents.ON_RESULT_SELECTED]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK]: {
          target: TRegularPlayStates.SELECT_PLAY_DESIGN,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.COPY_PREV_STATE_INPUT],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TRegularPlayStates.CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.REF_EVENT]: {
          target: TRegularPlayStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.SELECT_TOV]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          showLogo: true,
          showBackBtn: true,
        },
        onNext({ send, context, playFlowState, generalState, payload }) {
          const { currentPlay } = context;
          if (payload.value === TPlayResultTypes.FUMBLE_LOST) {
            currentPlay.resultType = pushResultType(context, TPlayResultTypes.FUMBLE_LOST);
          } else if (payload.value === TPlayResultTypes.INTERCEPTION) {
            currentPlay.resultType = pushResultType(context, TPlayResultTypes.INTERCEPTION);
          }

          send(TRegularPlayEvents.ON_RESULT_SELECTED, {
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TRegularPlayEvents.ON_TOV,
            },
            ctxUpdate: {
              currentPlay: {
                ...currentPlay,
                options: {
                  isTov: isTurnoverResult(last(currentPlay.resultType)),
                },
              },
            },
          });
        },
        onBack({ send }) {
          send(TRegularPlayEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TRegularPlayEvents.BACK]: {
          target: TRegularPlayStates.SELECT_PASS_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_RESULT_SELECTED]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.SELECT_YARDAGE]: {
      meta: {
        component: TInputTypes.YARDAGE_SELECTOR,
        componentConfiguration: {
          title: 'Play Result',
          showLogo: true,
          showBackBtn: true,
        },
        onNext(inp) {
          const { context, playFlowState, payload, send } = inp;
          const yardageEvt = payload as IYardageSelectionEvent;
          let resultType = (context.currentPlay?.resultType || []).pop();
          const isTurnover = isTurnoverResult(resultType);

          const { down: resultingDown, isTurnoverOnDowns } = getResultingDownAndDistance(
            playFlowState.presentStateBasedOnCurrentMode.down,
            playFlowState.presentStateBasedOnCurrentMode.distance,
            playFlowState.presentStateBasedOnCurrentMode.driveDirection,
            playFlowState.presentStateBasedOnCurrentMode.yardLine.number,
            playFlowState.presentStateBasedOnCurrentMode.yardLine.side,
            {
              ...context.currentPlay,
              postPlayYardLine: yardageEvt.yardLine,
              postPlaySide: yardageEvt.side,
            }
          );

          if (
            resultingDown === 1 &&
            !isTurnoverOnDowns &&
            !isTurnover &&
            !context.currentPlay.penalty &&
            !RESULT_TYPES_TO_KEEP_ON_FIRST_DOWN.includes(resultType)
          ) {
            resultType = TPlayResultTypes.FIRST_DOWN;
          }

          if (context.currentPlay.options?.isPenaltyOnTurnover && context.yardageToggleData) {
            context.yardageToggleData.toggles = context.yardageToggleData.toggles.filter(
              (t) => t !== TPlayResults.PENALTY
            );
            context.yardageToggleData.currToggle -= 1;
          }

          if (
            context.yardageToggleData &&
            context.yardageToggleData.toggles.length - 1 === context.yardageToggleData.currToggle
          ) {
            if (context.currentPlay.refEventOutcomeId === TRefEventOutcomes.REVIEW) {
              context.currentPlay.resultType.push(resultType);
            }
            return send(TMachineEvents.PLAY_COMPLETED, {
              nextStateInput: {
                backTo: TRegularPlayEvents.BACK_TO_PASS_RESULT,
              },
              ctxUpdate: {
                ...context,
                currentPlay: {
                  ...context.currentPlay,
                  postPlayYardLine: yardageEvt.yardLine,
                  postPlaySide: yardageEvt.side,
                },
              },
            });
          }

          return handleYardageToggle(inp, TMachineEvents.BACK_TO_YARDAGE_SELECTION, {
            side: (payload as IYardageSelectionEvent).side,
            yardLine: (payload as IYardageSelectionEvent).yardLine,
            resultType,
            currToggle: 0,
          });
        },
        onBack({ context, send }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TRegularPlayStates.CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_PLAY_DESIGN]: {
          target: TRegularPlayStates.SELECT_PLAY_DESIGN,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_FUMBLE_RECOVERY]: {
          target: TRegularPlayStates.SELECT_FUMBLE_RECOVERY,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_PASS_RESULT]: {
          target: TRegularPlayStates.SELECT_PASS_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_UNDER_REVIEW]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.UNDER_REVIEW}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_CHAIN]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.CHAINS}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_REF]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.SELECT_REF_TYPE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TRegularPlayStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TRegularPlayStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_TOV]: {
          target: TRegularPlayStates.SELECT_TOV,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TRegularPlayStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_SELECT_DRIVE]: {
          target: TRegularPlayStates.SELECT_DRIVE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.REF_EVENT]: REGULAR_PLAY_REF_WF,

    [TRegularPlayStates.HANDLE_YARDAGE_TOGGLES]: {
      meta: HANDLE_YARDAGE_TOGGLES_META,
      on: {
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.SELECT_YARDAGE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.UNSET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.BACK_TO_SAME]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.ON_POSSESSION_SELECT]: {
          target: TRegularPlayStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.POST_PENALTY_YARDAGE]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TRegularPlayStates.CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.CONFIRMATION]: {
      meta: CONFIRM_BASIC_META,
      on: {
        [TMachineEvents.CONFIRMED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.CONFIRM_WITH_SAFTEY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.TO_PAT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.ON_REF_EVENT]: {
          target: TRegularPlayStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE]: {
          target: TRegularPlayStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.BACK_TO_PASS_RESULT]: {
          target: TRegularPlayStates.SELECT_PASS_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.ON_SELECT_POSSESSION]: {
          target: TRegularPlayStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.SELECT_POSSESSION]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Possession',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ send, generalState, playFlowState, payload, context }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          if (payload.value === playFlowState.activeTeam) {
            // Possession to offense
            ctxUpdate.currentPlay.options.isPossessionRetained = true;

            return send(TRegularPlayEvents.TO_DRIVE_SELECT, {
              ctxUpdate,
              nextStateInput: {
                ...SELECT_DRIVE_INPUTS,
                backTo: TRegularPlayEvents.BACK_TO_SELECT_POSSESSION,
                title: 'Select Drive',
              },
            });
          }
          // Possession to defense
          ctxUpdate.currentPlay.options.isTov = true;
          ctxUpdate.currentPlay.options.isPenaltyOnTurnover = !!ctxUpdate.currentPlay.penalty;
          ctxUpdate.currentPlay.options.isPossessionRetained = false;
          return send(TRegularPlayEvents.TO_YARDAGE, {
            ctxUpdate,
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TRegularPlayEvents.BACK_TO_SELECT_POSSESSION,
              title: ctxUpdate.currentPlay.penalty ? 'Post-Penalty Yardline' : 'Post-Play Yardline',
            },
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.TO_YARDAGE]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.TO_DRIVE_SELECT]: {
          target: TRegularPlayStates.SELECT_DRIVE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_CONFIRMATION]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_REGULAR_PLAY}.${TRegularPlayStates.CONFIRMATION}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TRegularPlayStates.SELECT_DRIVE]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Drive',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ send, generalState, playFlowState, payload, context }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          if (payload.value === TPenaltyDriveResult.NEW) {
            ctxUpdate.currentPlay.options.isNewDrive = true;
          } else if (payload.value === TPenaltyDriveResult.CONTINUE) {
            ctxUpdate.currentPlay.options.isNewDrive = false;
          }

          if (ctxUpdate.currentPlay.penalty) {
            ctxUpdate.currentPlay.resultType = ctxUpdate.currentPlay.resultType.filter(
              (res) =>
                ![
                  TPlayResultTypes.PENALTY_AWAY,
                  TPlayResultTypes.PENALTY_HOME,
                  TPlayResultTypes.PENALTY_BOTH,
                  TPlayResultTypes.TOV,
                  TPlayResultTypes.INTERCEPTION,
                  TPlayResultTypes.FUMBLE_LOST,
                ].includes(res)
            );

            ctxUpdate.currentPlay.options.isPenaltyOnTurnover = true;
            ctxUpdate.currentPlay.options.isTov = false;
          }

          return send(TRegularPlayEvents.TO_YARDAGE, {
            ctxUpdate,
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TRegularPlayEvents.BACK_TO_SELECT_DRIVE,
              title: ctxUpdate.currentPlay.penalty ? 'Post-Penalty Yardline' : 'Post-Play Yardline',
            },
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRegularPlayEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TRegularPlayStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRegularPlayEvents.TO_YARDAGE]: {
          target: TRegularPlayStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
  },
};
